import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { flushSync } from "react-dom";
import MarkdownPreview from "@uiw/react-markdown-preview";
import type { IAlertConfig } from "./components/BlockquoteRenderer/BlockquoteRenderer";
import { createMarkdownComponents } from "./MarkdownElementRenderers";
import { ImageExpandedRenderer } from "./components/ImageExpandedRenderer/ImageExpandedRenderer";
import { TableExpandedRenderer } from "./components/TableExpandedRenderer/TableExpandedRenderer";
import { useTranslations } from "@/hooks/useTranslations";
import styles from "./MarkdownContent.module.scss";

interface IMarkdownContentProps {
  content: string;
  className?: string;
  containerClassName?: string;
  enableDebug?: boolean;
  onCopyLink?: (sectionId: string) => void;
  copiedSectionId?: string | null;
  mediaBaseUrl?: string;
  alertConfigs?: Record<string, IAlertConfig>;
}

export function MarkdownContent({ content, onCopyLink, copiedSectionId, containerClassName, mediaBaseUrl, alertConfigs }: IMarkdownContentProps): ReactNode {
  const { t } = useTranslations();
  const [expandedTableProps, setExpandedTableProps] = useState<React.TableHTMLAttributes<HTMLTableElement> | null>(null);
  const expandTableTriggerIndexRef = useRef<number>(-1);
  const markdownContainerRef = useRef<HTMLDivElement>(null);
  const [expandedImage, setExpandedImage] = useState<{ src: string; alt: string } | null>(null);
  const [isExpandedImageZoomed, setIsExpandedImageZoomed] = useState<boolean>(false);
  const [isExpandedImageDragging, setIsExpandedImageDragging] = useState<boolean>(false);
  const [expandedImagePan, setExpandedImagePan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const expandedImageDragState = useRef<{ startX: number; startY: number; baseX: number; baseY: number }>({
    startX: 0,
    startY: 0,
    baseX: 0,
    baseY: 0,
  });
  const expandedImageDidMove = useRef<boolean>(false);

  function sanitizeMarkdownContent(value: string): string {
    return value
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
      .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
      .replace(/\s(src|href)\s*=\s*("\s*(?:javascript|vbscript|data):[^"]*"|'\s*(?:javascript|vbscript|data):[^']*'|\s*(?:javascript|vbscript|data):[^\s>]+)/gi, "")
      .replace(/\[([^\]]+)\]\((javascript|vbscript|data):[^)]+\)/gi, "[$1](#)")
      .replace(/!\[([^\]]*)\]\((javascript|vbscript|data):[^)]+\)/gi, "![$1](#)");
  }

  const getIsDarkTheme = (): boolean => {
    if (typeof window === "undefined") {
      return false;
    }

    return document.documentElement.classList.contains("theme-dark");
  };

  const [isDarkTheme, setIsDarkTheme] = useState<boolean>(getIsDarkTheme);
  const isTableExpanded = expandedTableProps !== null;
  const isImageExpanded = expandedImage !== null;
  const isOverlayOpen = isTableExpanded || isImageExpanded;
  const expandTableLabel = t("contentPage.expandTable");
  const collapseTableLabel = t("contentPage.collapseTable");

  const closeExpandedTable = useCallback((): void => {
    const triggerIndex = expandTableTriggerIndexRef.current;
    flushSync(() => setExpandedTableProps(null));
    if (triggerIndex >= 0 && markdownContainerRef.current) {
      const buttons = markdownContainerRef.current.querySelectorAll<HTMLElement>(`button[aria-label="${expandTableLabel}"]`);
      buttons[triggerIndex]?.focus();
    }
    expandTableTriggerIndexRef.current = -1;
  }, [expandTableLabel]);

  const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

  useIsomorphicLayoutEffect((): (() => void) | void => {
    if (typeof window === "undefined") {
      return;
    }

    const rootElement = document.documentElement;
    const updateTheme = (): void => {
      const nextValue = rootElement.classList.contains("theme-dark");
      setIsDarkTheme((previous) => (previous === nextValue ? previous : nextValue));
    };

    updateTheme();

    const MutationObserverCtor = window.MutationObserver;
    if (!MutationObserverCtor) {
      return;
    }

    const observer = new MutationObserverCtor(updateTheme);
    observer.observe(rootElement, { attributes: true, attributeFilter: ["class"] });

    return (): void => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !isOverlayOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        if (isImageExpanded) {
          closeExpandedImage();
        }
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return (): void => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOverlayOpen, isImageExpanded]);

  const openExpandedImage = (src: string, alt: string): void => {
    setExpandedImage({ src, alt });
    setIsExpandedImageZoomed(false);
    setIsExpandedImageDragging(false);
    setExpandedImagePan({ x: 0, y: 0 });
    expandedImageDidMove.current = false;
    expandedImageDragState.current = { startX: 0, startY: 0, baseX: 0, baseY: 0 };
  };

  const closeExpandedImage = (): void => {
    setExpandedImage(null);
    setIsExpandedImageZoomed(false);
    setIsExpandedImageDragging(false);
    setExpandedImagePan({ x: 0, y: 0 });
    expandedImageDidMove.current = false;
    expandedImageDragState.current = { startX: 0, startY: 0, baseX: 0, baseY: 0 };
  };

  function resolveMediaUrl(url: string | undefined): string | undefined {
    if (!url) {
      return url;
    }

    const trimmedUrl = url.trim();
    const normalizedUrl = trimmedUrl.replace(/[\s\t\n\r]+/g, '');
    if (/^(javascript|vbscript|data):/i.test(normalizedUrl)) {
      return undefined;
    }

    // Already absolute (or protocol-relative) URLs are left untouched.
    if (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://") || trimmedUrl.startsWith("//")) {
      return trimmedUrl;
    }

    const trimmedMediaBaseUrl = mediaBaseUrl?.trim() ?? "";
    const base =
      // eslint-disable-next-line no-nested-ternary
      trimmedMediaBaseUrl !== ""
        ? trimmedMediaBaseUrl
        : typeof window !== "undefined"
          ? window.location.origin
          : "";

    if (base === "") {
      return trimmedUrl;
    }

    try {
      return new URL(trimmedUrl, base.endsWith("/") ? base : `${base}/`).toString();
    } catch {
      return trimmedUrl;
    }
  }
  
  const components = createMarkdownComponents({
    t,
    onCopyLink,
    copiedSectionId,
    expandTableLabel,
    setExpandedTableProps: (tableProps: React.TableHTMLAttributes<HTMLTableElement> | null): void => {
      if (tableProps !== null && markdownContainerRef.current) {
        const allButtons = markdownContainerRef.current.querySelectorAll<HTMLElement>(`button[aria-label="${expandTableLabel}"]`);
        expandTableTriggerIndexRef.current = Array.from(allButtons).indexOf(document.activeElement as HTMLElement);
      }
      setExpandedTableProps(tableProps);
    },
    resolveMediaUrl,
    openExpandedImage,
    alertConfigs,
  });

  return (
    <div className={`${styles.markdown} ${containerClassName || ""}`} ref={markdownContainerRef}>
      <MarkdownPreview
        source={sanitizeMarkdownContent(content)}
        components={components}
        wrapperElement={{ "data-color-mode": isDarkTheme ? "dark" : "light" }}
        style={{ backgroundColor: "transparent" }}
      />

      {isTableExpanded && expandedTableProps && (
        <TableExpandedRenderer
          expandedTableProps={expandedTableProps}
          collapseTableLabel={collapseTableLabel}
          isDarkTheme={isDarkTheme}
          onClose={closeExpandedTable}
        />
      )}

      {isImageExpanded && expandedImage && (
        <ImageExpandedRenderer
          expandedImage={expandedImage}
          isExpandedImageZoomed={isExpandedImageZoomed}
          isExpandedImageDragging={isExpandedImageDragging}
          expandedImagePan={expandedImagePan}
          expandedImageDidMoveRef={expandedImageDidMove}
          expandedImageDragStateRef={expandedImageDragState}
          closeExpandedImage={closeExpandedImage}
          setIsExpandedImageZoomed={setIsExpandedImageZoomed}
          setIsExpandedImageDragging={setIsExpandedImageDragging}
          setExpandedImagePan={setExpandedImagePan}
        />
      )}
    </div>
  );
}

